import { useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';

import { ToastLife } from '@/components/hoc/toast-container/enums';
import type { AccessToken } from '@/lib/tokenStorage';
import { useAccessToken } from '@/services/auth';

import { useToast } from '../toast';

export type ReportResponse = {
  url: string;
  expiresOn: string;
  blobName: string;
};

async function generateReport<T>(
  token: AccessToken,
  reportType: string,
  parameters: T,
  options?: { detailedErrors?: boolean; isForEmail?: boolean },
): Promise<ReportResponse> {
  const REPORT_SERVICE_URL = import.meta.env.VITE_REPORT_SERVICE_URL;
  if (!REPORT_SERVICE_URL) {
    throw new Error('VITE_REPORT_SERVICE_URL is not defined');
  }

  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const res = await fetch(
    new URL('/api/v1/reports/generate', REPORT_SERVICE_URL),
    {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        reportType,
        parameters,
        ...(options?.isForEmail && { isForEmail: true }),
      }),
    },
  );

  if (!res.ok) {
    if (res.status === 401) {
      throw new Error('Unauthorized. Please log in again.');
    }
    if (options?.detailedErrors) {
      const body = await res.text();
      throw new Error(body || res.statusText || 'Error generating report');
    }
    throw new Error(res.statusText || 'Error generating report');
  }

  const data: ReportResponse = await res.json();
  return data;
}

/**
 * Returns a custom React hook for generating reports of a specific type.
 *
 * @example
 * // Define the parameters type for your report
 * // Create the custom hook for your report type
 * const useMyReport = createReport<{ startDate: string; endDate: string }>('myReportType');
 *
 * // Use the hook in your component
 * function MyComponent() {
 *   const [generateReport, { isLoading, data, error }] = useMyReport();
 *
 *   const handleGenerate = async () => {
 *     await generateReport({ startDate: '2023-01-01', endDate: '2023-01-31' });
 *   };
 *
 *   // ...render logic
 * }
 */
export function createReport<T>(
  reportType: string,
  options?: { detailedErrors?: boolean; isForEmail?: boolean },
) {
  return () => {
    const toast = useToast();
    const token = useAccessToken();

    const mutation = useMutation({
      mutationFn: ({
        parameters,
        callOptions,
      }: {
        parameters: T;
        callOptions?: { isForEmail?: boolean };
      }) =>
        generateReport<T>(token, reportType, parameters, {
          ...options,
          ...callOptions,
        }),
      onMutate() {
        return toast.show({
          severity: 'info',
          summary: 'Generating report...',
          sticky: true,
        });
      },
      onSuccess(data, _variables, message) {
        if (data.url) {
          window.open(data.url, '_blank', 'noopener,noreferrer');

          toast.update(message, {
            severity: 'success',
            summary: 'Generated report',
            sticky: false,
            life: ToastLife.LONG,
          });
        }
      },
      onError(error, _variables, message) {
        toast.update(message!, {
          severity: 'error',
          summary: 'Error generating report',
          detail: error.message,
          sticky: false,
          life: ToastLife.LONG,
        });
      },
    });
    const generate = useCallback(
      (parameters: T, callOptions?: { isForEmail?: boolean }) =>
        mutation.mutateAsync({ parameters, callOptions }),
      [mutation],
    );
    return [generate, mutation] as const;
  };
}
